import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useQuery, useMutation, gql } from '@apollo/client';
import { getCookie } from '../../util.js';

// ------- QUERIES
import QUERY_EXPENSES from "../../graphql/queries/Expenses/QueryExpenses.js";

// ------- COMPONENTS
import LandingPage from "../../components/layout/LandingPage.js";
import NewExpense from "./NewExpense.js";
import CsvUpload from "../../components/layout/CsvUpload.js";
import { ExpenseImportModal } from "../../components/csv-import/modals";

import {
  ForeignKeyMultiSelectDropDownInput,
} from "../../inputs/index.js";

import {
  Pane,
  AddIcon,
  SideSheet,
  DocumentShareIcon,
  ImportIcon,
  Badge,
  Dialog
} from 'evergreen-ui';

const CREATE_EXPENSE_CATEGORY = gql`
  mutation CreateExpenseCategory($publisherId: ID!, $value: String!) {
    createExpenseCategory(publisherId: $publisherId, value: $value) {
      expenseCategory {
        id
        value
      }
    }
  }
`;

function Expenses() {

  // ------- STATES
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);

  const [expenses, setExpenses] = useState([]);
  const [count, setCount] = useState(0);
  const [pagecount, setPagecount] = useState(0);
  const [currpage, setCurrpage] = useState(1);
  const [newmode, setNewmode] = useState(false);
  const [csvmode, setCsvmode] = useState(false);
  const [advancedImportMode, setAdvancedImportMode] = useState(false);
  const [params, setParams] = useState({});
  const [sourcePageInfo, setSourcePageInfo] = useState(false)
  const [showExpenseCategoryDialog, setShowExpenseCategoryDialog] = useState(false);
  const [unknownExpenseCategories, setUnknownExpenseCategories] = useState([]);
  const [currentExpenseCategory, setCurrentExpenseCategory] = useState(null);

  // ------- HOOKS

  useEffect(() => {
    if (location.state?.query) {
      setParams(location.state.query);
      if (location.state.queryTitle) {
        setSourcePageInfo({
          title: location.state.queryTitle,
          sourceURL: location.state.sourceURL,
          sourcePageTitle: location.state.sourcePageTitle
        });
      }
    }
    
    // Check for productWorkId in URL params
    const productWorkId = searchParams.get('productWorkId');
    if (productWorkId) {
      setParams(prev => ({
        ...prev,
        product__product_work__id: productWorkId
      }));
    }
  }, [location.state, searchParams]);

  const { loading, error, data, refetch: _refetch } = useQuery(QUERY_EXPENSES, {
    variables: {
      publisher: String(currentpublisher.id),
      page: parseInt(currpage),
      params: JSON.stringify(params)
    },
  });

  const refetch = useCallback(() => { setTimeout(() => _refetch(), 0) }, [_refetch]);

  useEffect(() => {
    if (data) {
      setExpenses(data.expenses);
      const csrftoken = getCookie('csrftoken');
      fetch('/count_expenses/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        },
        body: JSON.stringify({ publisher: currentpublisher.id })
      })
        .then(res => res.json())
        .then(data => {
          setCount(parseInt(data.message));
          setPagecount(Math.ceil(data.message / 25));
        })
    }
  }, [currentpublisher.id, data, navigate]);

  useEffect(() => {
    if (location.state) {
      let locState = location.state;
      setSourcePageInfo(locState)
    }
  }, [location.state]);

  const [createExpenseCategory] = useMutation(CREATE_EXPENSE_CATEGORY);

  // ------- FUNCTIONS
  const handleRefetchExpenses = () => {
    setParams({});
    refetch();
  }

  const handleToggleNewMode = () => {
    setNewmode(!newmode);
  }

  const handleToggleCsvMode = () => {
    setCsvmode(!csvmode);
    refetch();
  }

  const handleToggleAdvancedImportMode = () => {
    setAdvancedImportMode(!advancedImportMode);
  }

  const handleAddExpenseCategory = async (expenseCategory) => {
    try {
      const { data } = await createExpenseCategory({
        variables: {
          value: expenseCategory,
          publisherId: currentpublisher.id.toString()
        }
      });

      if (data.createExpenseCategory.expenseCategory) {
        setUnknownExpenseCategories(prev => prev.filter(ec => ec !== expenseCategory));
        if (unknownExpenseCategories.length <= 1) {
          setShowExpenseCategoryDialog(false);
        } else {
          setCurrentExpenseCategory(unknownExpenseCategories[1]);
        }
      }
    } catch (error) {
      console.error('Error adding expense category:', error);
    }
  };

  // ------- INPUTS

  const buttons = [
    {
      title: sourcePageInfo ? `Back to ${sourcePageInfo.sourcePageTitle}` : "Back",
      text: sourcePageInfo ? `Back to ${sourcePageInfo.sourcePageTitle}` : "Back",
      onClick: () => sourcePageInfo ? navigate(sourcePageInfo.sourceURL) : null,
      icon: "arrow-left",
      show: sourcePageInfo ? true : false
    },
    {
      title: "NEW EXPENSE",
      text: "New Expense",
      onClick: handleToggleNewMode,
      icon: AddIcon,
      show: true
    },
    // {
    //   title: "IMPORT EXPENSES",
    //   text: "CSV Upload",
    //   onClick: () => setCsvmode(true),
    //   icon: DocumentShareIcon,
    //   show: true
    // },
    {
      title: "IMPORT EXPENSES",
      text: "Advanced Import",
      onClick: handleToggleAdvancedImportMode,
      icon: ImportIcon,
      show: true
    }
  ].filter(button => button.show);

  const cols = [
    { title: "DATE", type: "button", flex: "1.5", query: "expense_date" },
    { title: "AMOUNT", type: "button", flex: "1.5", query: "amount", align: "right" },
    { title: "PRODUCT", type: "button", flex: "2.5", query: "product__title" },
    { title: "PRODUCT FORMAT", type: "button", flex: "2.5", query: "product__format__description" },
    { title: "CATEGORY", type: "button", flex: "2", query: "category__value" },
    { title: "NOTES", type: "button", flex: "3", query: "admin_notes" },
  ];

  const searchInit = {
    all: "",
    product__title: "",
    admin_notes: "",
    category__value: "",
  };

  const searchInputs = {
    default: { searchq: "all", dropLabel: "Default" },
    product__title: { searchq: "product__title", dropLabel: "Product Title" },
    admin_notes: { searchq: "admin_notes", dropLabel: "Notes" },

  };
  const filterInputs = {
    category__value: { comp: ForeignKeyMultiSelectDropDownInput, formLabel: "Category", group: 1, values: publisherGlobalData.expensecategorys },
  };

  const csvFields = [
    "adminNotes",
    "amount",
    "category",
    "expenseDate",
    "id",
    "modified",
    "product",

  ];

  const csvConfig = {
    "category": ["value"],
    "product": ["id", "title", "isbn"],
    "sets": {},
    "primarySets": {},
    "arrays": {}
  };

  // if you make changes to these arrays, be sure to update the lists in views.py as well
  const csvTemplate = [
    [
      "product",
      "isbn",
      "amount",
      "expense_date",
      "category",
      "admin_notes"
    ],
    [
      "The WorkingLit product ID (either ID or ISBN required if no category)",
      "Product ISBN (either ID or ISBN required if no category)",
      "Must be a decimal (only digits and one period allowed)",
      "yyyy-mm-dd",
      "The exact name of the category as it exists in WorkingLit",
      ""
    ],
    [
      "1",
      "",
      "105.99",
      "2021-12-19",
      "Marketing",
      "Mailers for bookstores"
    ]
  ];

  const csvWidths = [
    2,
    3,
    2,
    3,
    4,
    4
  ]

  if (error) return `Error! ${error}`;


  return (
    <Pane position="absolute" padding={16} width="100%" height="100%">
      {
        csvmode ? (
          <>
            <CsvUpload
              handleToggleCsvMode={handleToggleCsvMode}
              template={csvTemplate}
              itemType="expense"
              tableWidth={1000}
              colWidths={csvWidths}
              onValidationError={(errors) => {
                if (errors.unknown_expense_categories) {
                  setUnknownExpenseCategories(errors.unknown_expense_categories);
                  setCurrentExpenseCategory(errors.unknown_expense_categories[0]);
                  setShowExpenseCategoryDialog(true);
                }
              }}
            />

            <Dialog
              isShown={showExpenseCategoryDialog}
              title="Unknown Expense Category"
              onCloseComplete={() => setShowExpenseCategoryDialog(false)}
              confirmLabel="Yes, please add it!"
              cancelLabel="Cancel, I will fix the spreadsheet"
              onConfirm={() => handleAddExpenseCategory(currentExpenseCategory)}
              hasFooter={true}
              hasHeader={true}
              intent="none"
              width="560px"
              topOffset="80px"
              sideOffset="80px"
              confirmAppearance="dialogConfirm"
              cancelAppearance="dialogCancel"
            >
              This expense category "{currentExpenseCategory}" was not found in your preferences. Would you like us to add it?
            </Dialog>
          </>
        ) : (
          <Pane>
            {newmode && (
              <SideSheet
                isShown={newmode}
                onCloseComplete={() => setNewmode(false)}
                preventBodyScrolling
              >
                <NewExpense
                  handleToggleNewMode={handleToggleNewMode}
                  handleRefetchExpenses={refetch}
                  params={location.state?.query || {}}
                />
              </SideSheet>
            )}
            <LandingPage
              title={sourcePageInfo?.title || "Expenses"}
              location={location}
              sourcePageInfo={sourcePageInfo}
              data={expenses}
              loading={loading}
              error={error}
              handleRefetch={handleRefetchExpenses}
              buttons={buttons}
              count={count}
              pagecount={pagecount}
              searchInputs={searchInputs}
              filterInputs={filterInputs}
              searchInit={searchInit}
              csvConfig={csvConfig}
              csvFields={csvFields}
              csvType="Expenses"
              cols={cols}
              refetch={refetch}
              setParams={setParams}
              RowComponent={ExpenseRow}
              currpage={currpage}
              setCurrpage={setCurrpage}
              colWidths={["0.01"].concat(cols.map((col) => col.flex))}
            />
            <ExpenseImportModal isShown={advancedImportMode} onClose={handleToggleAdvancedImportMode} />
          </Pane>
        )}
    </Pane>
  )
}

function ExpenseRow({ item, allmode, handleCheck, colWidths, makeIDCell, makeTableCellArray }) {
  const [checked, setChecked] = useState(false);
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);

  const handleSetChecked = () => {
    handleCheck(checked, item)
    setChecked(!checked);
  }
  const product = item.product;
  const currency = publisherGlobalData.currency
    ? publisherGlobalData.currency.symbol
    : "$";

  const formattedAmount = item.amount ? parseFloat(item.amount).toFixed(2) : '0.00';

  const rowCells = [
    {
      content: makeIDCell(checked, handleSetChecked, item.id), disableSelect: true
    },
    { content: item.expenseDate },
    { isNumber: true, content: <Pane>{currency}{formattedAmount}</Pane> },
    { content: product && product.productWork ? product.productWork.title : '(No Product Title)' },
    { content: !product ? "(No Product)" : product.format ? <Badge color="neutral" marginBottom={8}> {product.format.description}</Badge> : "(No Format)" },
    { content: item.category && item.category.value },
    { content: item.adminNotes },
  ];

  useEffect(() => {
    setChecked(allmode);
  }, [allmode]);

  const cellArray = makeTableCellArray(rowCells, colWidths, item.id);

  return cellArray;
}

export default Expenses;
